import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Heading, Img, Button, Text, Switch } from "../../components";
import CarCardTemplate from "components/CarCardTemplate";
import AccesoriesCheckList from "../AccesoriesCheckList";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { he } from "date-fns/locale";
import Alert from "../Alert";
import carIcon from "../../asstes/images/car-icon.svg";
import sideImage from "../../asstes/images/add-car-left.svg";
import { API_DOMAIN, API_DOMAIN_IMG } from "config";
import { uploadCarImages, uploadCarVideo } from "../../api/carMedia";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("he", he);
setDefaultLocale("he");

const API_BASE = String(API_DOMAIN || "").replace(/\/+$/, "");
const apiUrl = (path) => `${API_BASE}${path}`;

const Field = React.memo(({
  name,
  label,
  icon,
  type = "text",
  value,
  onChange,
  onBlur,
}) => {
  return (
    <div className="wrp-car-field">
      <div className="label-wrap">
        {icon && <img src={icon} alt="" />}
        <label htmlFor={name}>{label}</label>
      </div>
      {type === "date" ? (
        <DatePicker
          withPortal
          selected={value}
          onChange={onChange}
          onBlur={onBlur}
          locale="he"
          dateFormat="dd/MM/yyyy"
          popperPlacement="bottom-end"
          popperContainer={({ children }) => (
            <div style={{
              position: "absolute",
              zIndex: 10000,
              overflow: "visible"
            }}>{children}</div>
          )}
          showPopperArrow={false}
          className="order rounded px-2 py-1 focus:outline-none focus:border-blue-500"
          id={name}
          onChangeRaw={e => e.preventDefault()}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value?.toString() ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          className="border rounded px-2 py-1 focus:outline-none focus:border-blue-500"
        />
      )}
    </div>
  );
});

function EditCar({ car: initialCar, currentUser, onClose, onCarUpdated }) {
  const [car, setCar] = useState(initialCar); // תמיד עובד רק עם ה־prop
const navigate = useNavigate();
const [imagesToDelete, setImagesToDelete] = useState([]);
const imageRequestVersionRef = React.useRef(Date.now());

  const resolveImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (/^https?:\/\//i.test(imagePath)) return imagePath;

    const cleanBase = String(API_DOMAIN || "").replace(/\/+$/, "");
    const cleanPath = String(imagePath).replace(/^\/+/, "");
    const src = `${cleanBase}/${cleanPath}`;
    return `${src}${src.includes("?") ? "&" : "?"}v=${imageRequestVersionRef.current}`;
  };

  const appendCacheBust = (imagePath) => {
    if (!imagePath) return imagePath;
    const separator = String(imagePath).includes("?") ? "&" : "?";
    return `${imagePath}${separator}v=${Date.now()}`;
  };

  const [selected, setSelected] = useState(initialCar?.accessories || []);
  const [originalImages, setOriginalImages] = useState(initialCar?.images || []);
  const [videoUrl, setVideoUrl] = useState(initialCar?.video_url || null);

  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState(1);
  const [index, setIndex] = useState(1);
  const [form, setForm] = useState({
    saleTypes: [],
    price: "",
  });

  // ערך התחלתי ריק
  const emptyCar = {
    user_id: currentUser?.id,
    license_plate: "",
    year: "",
    manufacturer: "",
    model: "",
    sub_model: "",
    accident_type: "",
    damage_estimate: "",
    asking_price: "",
    status: 1,
    transmission: "",
    hand: "",
    car_type: "",
    kilometer: "",
    gearbox: "",
    original_ownership: "",
    annual_registration_fee: "",
    finish_level: "",
    engine_capacity: "",
    engine_type: "",
    chassis_number: "",
    accessories: "",
    entry_date: "",
    pickup_date: "",
    last_payment_date: "",
    color: "",
    engine_number: "",
    insert_date: "",
    is_auction: false,
    sale_type_id: null,
  };

  const [images, setImages] = useState([]); // Files or URLs
  const [videoFile, setVideoFile] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("error");
  const [rotatingNewIndexes, setRotatingNewIndexes] = useState([]);
  const [rotatingOldIndexes, setRotatingOldIndexes] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [draggingGalleryId, setDraggingGalleryId] = useState(null);
  const newImageCounterRef = React.useRef(0);

  const loadImageElement = (source) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("טעינת התמונה נכשלה"));
      img.src = source;
    });

  const canvasToBlob = (canvas, mimeType) =>
    new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("יצירת קובץ מסובב נכשלה"));
          return;
        }
        resolve(blob);
      }, mimeType);
    });

  const rotateImageSource90 = async (source, sourceType = "image/jpeg", fileBaseName = "rotated-image") => {
    const img = await loadImageElement(source);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("לא ניתן לסובב את התמונה");
    }

    canvas.width = img.height;
    canvas.height = img.width;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    const outputType = sourceType === "image/svg+xml" ? "image/png" : (sourceType || "image/jpeg");
    const rotatedBlob = await canvasToBlob(canvas, outputType);
    const extension = outputType === "image/png" ? ".png" : ".jpg";

    return new File([rotatedBlob], `${fileBaseName}-rotated${extension}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  };
  const getCarById = async (id) => {
    try {
      const { data } = await axios.get(
        apiUrl(`/car/getCarById/${id}`),
        { params: { user_id: currentUser.id } }
      );

      const carData = {
        ...data,
        accessories: typeof data.accessories === "string"
          ? JSON.parse(data.accessories)
          : data.accessories || [],
      };

      setCar(carData);
      setSelected(carData.accessories);
      return carData;
    } catch (err) {
      console.error("שגיאה בשליפת רכב:", err);
      return null;
    }
  };

useEffect(() => {
  setCar(initialCar);
  setSelected(initialCar?.accessories || []);
  setOriginalImages(initialCar?.images || []);
  setGalleryItems((initialCar?.images || []).map((img) => ({
    ...img,
    _kind: "old",
    _gid: `old-${img.id}`,
  })));
  setImagesToDelete([]);
  setVideoUrl(initialCar?.video_url || null);
}, [initialCar]);



  const handleDelete = (indexToDelete) => {
    setGalleryItems((prev) => {
      const target = prev[indexToDelete];
      if (target?._kind === "old" && target?.id) {
        setImagesToDelete((old) => (old.includes(target.id) ? old : [...old, target.id]));
      }
      return prev.filter((_, i) => i !== indexToDelete);
    });
  };
  useEffect(() => {
  if (initialCar.sale_type_id === 1) {
    setForm(f => ({ ...f, saleTypes: ["unload"] }));
  } else if (initialCar.sale_type_id === 2) {
    setForm(f => ({ ...f, saleTypes: ["marketing"] }));
  } else {
    setForm(f => ({ ...f, saleTypes: [] }));
  }
}, [initialCar.sale_type_id]);


  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setGalleryItems((prev) => {
      const room = Math.max(0, 20 - prev.length);
      const nextFiles = selectedFiles.slice(0, room).map((file) => ({
        _kind: "new",
        _gid: `new-${Date.now()}-${newImageCounterRef.current++}`,
        file,
      }));
      return [...prev, ...nextFiles];
    });
    setShowAlert(false);
    e.target.value = "";
  };

  const handleRotateImage = async (indexToRotate) => {
    const imageToRotate = galleryItems[indexToRotate];
    if (!imageToRotate) return;

    setShowAlert(false);

    if (imageToRotate._kind === "new") {
      const sourceFile = imageToRotate.file;
      if (!(sourceFile instanceof File)) return;

      setRotatingNewIndexes((prev) => [...prev, imageToRotate._gid]);
      const objectUrl = URL.createObjectURL(sourceFile);
      try {
        const rotatedFile = await rotateImageSource90(
          objectUrl,
          sourceFile.type,
          (sourceFile.name || "new-image").replace(/\.[^.]+$/, "")
        );

        setGalleryItems((prev) =>
          prev.map((item, idx) =>
            idx === indexToRotate && item._kind === "new"
              ? { ...item, file: rotatedFile }
              : item
          )
        );
      } catch (err) {
        console.error(err);
        setAlertMessage("לא הצלחנו לסובב את התמונה. נסה שוב.");
        setAlertType("error");
        setShowAlert(true);
      } finally {
        URL.revokeObjectURL(objectUrl);
        setRotatingNewIndexes((prev) => prev.filter((id) => id !== imageToRotate._gid));
      }
      return;
    }

    if (!imageToRotate?.id || !imageToRotate?.image_url) return;

    setRotatingOldIndexes((prev) => [...prev, imageToRotate.id]);
    try {
      await axios.post(apiUrl("/car/rotateImage"), {
        image_id: imageToRotate.id,
        degrees: 90,
      });

      setGalleryItems((prev) =>
        prev.map((item, idx) =>
          idx === indexToRotate && item._kind === "old"
            ? { ...item, image_url: appendCacheBust(item.image_url) }
            : item
        )
      );
    } catch (err) {
      console.error(err);
      setAlertMessage("לא הצלחנו לסובב את התמונה הקיימת. נסה שוב.");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setRotatingOldIndexes((prev) => prev.filter((id) => id !== imageToRotate.id));
    }
  };

  const handleGalleryDragStart = (gid) => {
    setDraggingGalleryId(gid);
  };

  const handleGalleryDrop = (targetGid) => {
    setGalleryItems((prev) => {
      if (!draggingGalleryId || draggingGalleryId === targetGid) return prev;

      const from = prev.findIndex((item) => item._gid === draggingGalleryId);
      const to = prev.findIndex((item) => item._gid === targetGid);
      if (from < 0 || to < 0) return prev;

      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDraggingGalleryId(null);
  };

  const handleGalleryDragEnd = () => {
    setDraggingGalleryId(null);
  };

  const handleSetAsPrimary = (gid) => {
    setGalleryItems((prev) => {
      const from = prev.findIndex((item) => item._gid === gid);
      if (from <= 0) return prev;

      const next = [...prev];
      const [picked] = next.splice(from, 1);
      next.unshift(picked);
      return next;
    });
  };

  const handleVideoChange = (e) => {
    setShowAlert(false);
    const file = e.target.files[0];
    if (!file) return;
    const MAX_VIDEO_SIZE_MB = 2;
    const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      setAlertMessage(`הקובץ גדול מדי. מקסימום מותר: ${MAX_VIDEO_SIZE_MB}MB`);
      setAlertType("error");
      setShowAlert(true);
      e.target.value = "";
      return;
    }
    setVideoFile(file);
    setVideoUrl(null); // hide old
  };

  const getRequestErrorMessage = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data;

    if (typeof data === "string") {
      if (status === 413) {
        return "קבצי התמונות גדולים מדי או שהשרת דחה את בקשת ההעלאה.";
      }
      if (data.includes("413 Request Entity Too Large")) {
        return "קבצי התמונות גדולים מדי עבור השרת.";
      }
      return `שגיאת שרת${status ? ` (${status})` : ""} בזמן העלאת התמונות`;
    }

    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (data?.details) return data.details;

    if (status === 413) {
      return "קבצי התמונות גדולים מדי או שהשרת דחה את בקשת ההעלאה.";
    }

    if (err?.message === "Network Error") {
      return "השרת לא החזיר JSON תקין עבור העלאת התמונות. בדקי את לשונית Network > Response.";
    }

    return err?.message || "שגיאה בשמירה";
  };

  const toggleSaleType = (type) => {
    const id = type === "unload" ? 1 : 2;
    setCar((prev) => ({
      ...prev,
      sale_type_id: id,
    }));
    setForm((prev) => ({
      ...prev,
      saleTypes: [type],
    }));
  };
const handleSubmit = async (e) => {
  e?.preventDefault?.();

  try {
    // 1. עדכון רכב
    const updatedCar = { ...car, accessories: selected };

    // 2. שמירה בשרת
    await axios.post(apiUrl("/car/updateCar"), {
      ...updatedCar,
      car_id: car?.id
    }, {
      headers: { "Content-Type": "application/json" }
    });

    const orderedNewItems = galleryItems.filter((item) => item._kind === "new");
    let uploadImagesResponse = null;

    if (orderedNewItems.length > 0 || imagesToDelete.length > 0) {
      uploadImagesResponse = await uploadCarImages({
        carId: car.id,
        files: orderedNewItems.map((item) => item.file),
        imageIdsToDelete: imagesToDelete,
      });
    }

    if (videoFile) {
      await uploadCarVideo({
        carId: car.id,
        videoFile,
      });
    }

    const uploadedDetails = uploadImagesResponse?.data?.uploaded_details || [];
    const uploadedIdQueue = [...uploadedDetails]
      .sort((a, b) => Number(a.input_index) - Number(b.input_index))
      .map((item) => Number(item.image_id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (orderedNewItems.length > 0 && uploadedIdQueue.length !== orderedNewItems.length) {
      throw new Error("חלק מהתמונות החדשות לא נשמרו, נסה לשמור שוב.");
    }

    const finalOrderIds = [];
    galleryItems.forEach((item) => {
      if (item._kind === "old") {
        finalOrderIds.push(Number(item.id));
        return;
      }
      const newId = uploadedIdQueue.shift();
      if (newId) {
        finalOrderIds.push(newId);
      }
    });

    if (finalOrderIds.length > 0) {
      await axios.post(apiUrl("/car/reorderImages"), {
        car_id: car.id,
        imageOrder: finalOrderIds,
      }, {
        headers: { "Content-Type": "application/json" }
      });
    }

    // 5. שליפה מחדש של הנתונים העדכניים באמצעות getCarById!
    const freshCar = await getCarById(car.id);
    if (freshCar) {
      setOriginalImages(freshCar.images || []);
      setGalleryItems((freshCar.images || []).map((img) => ({
        ...img,
        _kind: "old",
        _gid: `old-${img.id}`,
      })));
      setImages([]);
      setImagesToDelete([]);
      setVideoUrl(freshCar.video_url || null);
      setVideoFile(null);
    }

    setAlertMessage("הרכב עודכן בהצלחה");
    setAlertType("success");
    setShowAlert(true);

    // 6. עדכון בדף הראשי (הורה)
    if (onCarUpdated) onCarUpdated(freshCar);

    // 7. סגור פופאפ
    if (onClose) onClose();

  } catch (err) {
    const msg = getRequestErrorMessage(err);
    setAlertMessage(msg);
    setAlertType("error");
    setShowAlert(true);
    console.error("EditCar handleSubmit error:", err);
  }
};


  const handleCarChange = (e) => {
    setShowAlert(false);
    const { name, value } = e.target;
    setCar((prev) => ({ ...prev, [name]: value }));
  };

  const nextStep = () => setStep((step) => Math.min(step + 1, 5));
  const prevStep = () => setStep((step) => Math.max(step - 1, 1));
  const toggleTab = (tabIndex) => setActiveTab(activeTab === tabIndex ? null : tabIndex);
  const handleMoveToVideo = () => {
  setShowAlert(false);

  // בודק אם יש לפחות תמונה אחת (חדשה או קיימת)
  if (galleryItems.length < 1) {
    setAlertMessage("יש להעלות לפחות תמונה אחת עבור המודעה");
    setAlertType("error");
    setShowAlert(true);
    return;
  }

  setStep(3); // עובר לשלב הווידאו רק אם יש תמונה
};

  const RotateIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
      <path
        d="M20 11a8 8 0 1 1-2.34-5.66L20 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 4v4h-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const DragHandleIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
      <path d="M8 5v14M12 5v14M16 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  return (
    <div className="popup-overlay">
      {showAlert && (
        <Alert message={alertMessage} type={alertType} onClose={() => setShowAlert(false)} />
      )}
      <div className="popup-container">
        <div className="left-section-img">
          <img src={sideImage} alt="Car and key" className="car-image" />
          <img src="/images/close-car-pop-icon.svg" alt="close" className="close-car-pop" onClick={onClose} />
        </div>
        <div className="right-section-content">
          {/* Step 1: טופס */}
          {step === 1 && (
            <div className="popup-content">
              <h2 className="add-car-title">עריכת פרטי הרכב</h2>
              <h3 className="sub-title-upload-images">עדכן את הפרטים</h3>
              <div className="header"></div>
              <div className="scrol-wrapper" style={{ height: "calc(100vh - 45vh)" }}>
                <section className="car-details-container mt-6">
                  {/* פרטי רכב (טאבים) */}
                  <div className="tab">
                    <div
                      onClick={() => toggleTab(1)}
                      style={{ backgroundColor: activeTab === 1 ? "#f7f7f7" : "" }}
                      className={`cursor-pointer flex justify-center items-center self-stretch gap-[50px] p-5 border-blue_gray-100 border border-solid bg-secondary-primary_white flex-1 rounded-md`}
                    >
                      <div className="flex flex-1 justify-start">
                        <Heading size="ass_20" as="h5" className="text-[20px] font-semibold">פרטי הרכב</Heading>
                      </div>
                      <Img
                        src={
                          activeTab === index
                            ? `/images/img_checkmark_black_900_03_1.svg`
                            : `/images/img_arrow_left_secondary__primary_white_22x22.svg`
                        }
                        className="h-[22px] self-start"
                      />
                    </div>
                    {activeTab === index && (
                      <div className="flex items-start p-2 md:flex-col" dir="rtl">
                        <div className="car-details-grid">
                          <Field name="license_plate" label="מס' רישוי" icon={carIcon} value={car.license_plate} onChange={handleCarChange} />
                          <Field name="year" label="שנת יצור" icon="/images/img_calendar.svg" type="number" value={car.year} onChange={handleCarChange} />
                                                    <Field name="kilometer" label="קילומטראז'" icon="/images/img_artboard_2_copy_6.svg" value={car.kilometer} onChange={handleCarChange} />

                        
                        </div>
                        <div className="car-details-grid">
                          <Field name="manufacturer" label="יצרן" icon="/images/img_signal_black_900_03_1.svg" value={car.manufacturer} onChange={handleCarChange} />
                          <Field name="sub_model" label="תת דגם" icon={carIcon} value={car.sub_model} onChange={handleCarChange} />
                          <Field name="original_ownership" label="בעלות מקורית" icon="/images/img_artboard_2_copy_3_black_900_03_3.svg" value={car.original_ownership} onChange={handleCarChange} />
                        </div>
                        <div className="car-details-grid">
                          <Field name="model" label="דגם" icon={carIcon} value={car.model} onChange={handleCarChange} />
                          <Field name="hand" label="יד" icon="/images/img_artboard_2_copy_3_black_900_03_3.svg" value={car.hand} onChange={handleCarChange} />
                  </div>
                      </div>
                    )}
                  </div>
                  {/* טאב 2 - מפרט טכני */}
                  <div className="tab">
                    <div
                      onClick={() => toggleTab(2)}
                      style={{ backgroundColor: activeTab === 2 ? "#f7f7f7" : "" }}
                      className={`cursor-pointer flex justify-center items-center self-stretch gap-[50px] p-5 border-blue_gray-100 border border-solid bg-secondary-primary_white flex-1 rounded-md`}
                    >
                      <div className="flex flex-1 justify-start">
                        <Heading size="ass_20" as="h5" className="text-[20px] font-semibold">מפרט טכני</Heading>
                      </div>
                      <Img
                        src={
                          activeTab === 2
                            ? `/images/img_checkmark_black_900_03_1.svg`
                            : `/images/img_arrow_left_secondary__primary_white_22x22.svg`
                        }
                        className="h-[22px] self-start"
                      />
                    </div>
                    {activeTab === 2 && (
                      <div className="flex items-start p-2 md:flex-col" dir="rtl">
                        <div className="car-details-grid">
                          <Field name="color" label="צבע" icon="/images/img_contrast_black_900_03_4.svg" value={car.color} onChange={handleCarChange} />
                          <Field name="finish_level" label="רמת גימור"icon={carIcon} value={car.finish_level} onChange={handleCarChange} />
                        </div>
                        <div className="car-details-grid">
                          <Field name="chassis_number" label="מס' שילדה" icon={carIcon} value={car.chassis_number} onChange={handleCarChange} />
                          <Field name="gearbox" label="תיבת הילוכים" icon="/images/gearbox.svg" value={car.gearbox} onChange={handleCarChange} />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* מנוע וביצועים */}
                  <div className="tab">
                    <div
                      onClick={() => toggleTab(3)}
                      style={{ backgroundColor: activeTab === 3 ? "#f7f7f7" : "" }}
                      className={`cursor-pointer flex justify-center items-center self-stretch gap-[50px] p-5 border-blue_gray-100 border border-solid bg-secondary-primary_white flex-1 rounded-md`}
                    >
                      <div className="flex flex-1 justify-start">
                        <Heading size="ass_20" as="h5" className="text-[20px] font-semibold">מנוע וביצועים</Heading>
                      </div>
                      <Img
                        src={
                          activeTab === 3
                            ? `/images/img_checkmark_black_900_03_1.svg`
                            : `/images/img_arrow_left_secondary__primary_white_22x22.svg`
                        }
                        className="h-[22px] self-start"
                      />
                    </div>
                    {activeTab === 3 && (
                      <div dir="rtl">
                        <div className="car-details-grid">
                     {/* <Field name="engine_number" label="מס' מנוע" icon={carIcon} value={car.engine_number} onChange={handleCarChange} />*/}
                          <Field name="engine_type" label="סוג מנוע" icon={carIcon} value={car.engine_type} onChange={handleCarChange} />
                        </div>
                        <div className="car-details-grid">
                          <Field name="engine_capacity" label="נפח מנוע" icon={carIcon} value={car.engine_capacity} onChange={handleCarChange} />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* טאב 4 - אבזור ונוחות */}
                  <div className="tab">
                    <div
                      onClick={() => toggleTab(4)}
                      style={{ backgroundColor: activeTab === 4 ? "#f7f7f7" : "" }}
                      className={`cursor-pointer flex justify-center items-center self-stretch gap-[50px] p-5 border-blue_gray-100 border border-solid bg-secondary-primary_white flex-1 rounded-md`}
                    >
                      <div className="flex flex-1 justify-start">
                        <Heading size="ass_20" as="h5" className="text-[20px] font-semibold">אבזור ונוחות</Heading>
                      </div>
                      <Img
                        src={
                          activeTab === 4
                            ? `/images/img_checkmark_black_900_03_1.svg`
                            : `/images/img_arrow_left_secondary__primary_white_22x22.svg`
                        }
                        className="h-[22px] self-start"
                      />
                    </div>
                    {activeTab === 4 && (
                      <div className="flex items-start p-2 md:flex-col" dir="rtl">
                        <div className="tab-content">
                          <AccesoriesCheckList selected={selected} setSelected={setSelected} />
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </div>
              {/* כפתורי שלבים */}
              <div style={{ zIndex: "99" }} className="flex items-center justify-center gap-4 popup-footer">
              
                <button
                  onClick={nextStep}
                  type="button"
                  style={{ color: "white" }}
                  className="flex items-center gap-1 rounded-full bg-red-600 px-4 py-2 text-white text-sm font-semibold"
                ><span className="text-lg leading-none">‹</span>
                  הבא 
                </button> 
                 <button onClick={onClose} type="button" className="flex items-center gap-1 rounded-full border border-gray-400 px-4 py-2 text-sm font-semibold">
                   הקודם
                <span className="text-lg leading-none">›</span></button>
              </div>
            </div>
          )}
          {/* Step 2: תמונות */}
          {step === 2 && (
            <div dir="rtl" className="min-h-screen bg-white flex flex-col items-center py-5 px-4">
              <h2 className="add-car-title">עריכת תמונות רכב</h2>
              <h3 className="sub-title-upload-images">צלם, העלה והסר תמונות לגלריה</h3>
              <p className="text-xs text-gray-600 mb-2">גררו כדי לשנות סדר. תמונה מספר 1 היא התמונה הראשית.</p>
              <div className="w-full flex flex-row gap-6 py-10">
                <label className="w-1/3 border border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center upload-area2 cursor-pointer">
                  <input type="file" accept="image/jpeg,image/png,image/svg+xml" multiple className="hidden" onChange={handleFileChange} />
                  <b>גרור או לחץ על התיבה כדי להוסיף תמונות</b>
                  <div className="mb-2 mt-3">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 16l4-5h-3V4h-2v7H8z" />
                      <path d="M5 20h14v-2H5z" />
                    </svg>
                  </div>
                  <p className="text-xs text-red-500 text-center">
                    הקובץ צריך להיות עד 150KB בפורמטים: JPEG, PNG, SVG
                  </p>
                </label>
                <div className="grid grid-cols-3 gap-1 flex-1" style={{ maxHeight: '32vh', overflowY: 'auto' }}>
                  {galleryItems.map((item, i) => (
                    <div
                      key={item._gid}
                      style={{ height: '15vh', position: 'relative', background: '#e5e7eb', overflow: 'hidden' }}
                      draggable
                      onDragStart={() => handleGalleryDragStart(item._gid)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleGalleryDrop(item._gid)}
                      onDragEnd={handleGalleryDragEnd}
                    >
                      <img
                        src={item._kind === 'old' ? resolveImageUrl(item.image_url) : URL.createObjectURL(item.file)}
                        alt={`img-${i}`}
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      />
                      {/* מספר סידורי */}
                      <div style={{position:'absolute',top:4,left:4,zIndex:20,width:24,height:24,borderRadius:'50%',background:'#1e3a5f',color:'#fff',fontSize:11,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 1px 3px rgba(0,0,0,.3)'}}>
                        {i + 1}
                      </div>
                      {/* ידית גרירה */}
                      <div style={{position:'absolute',top:4,left:'50%',transform:'translateX(-50%)',zIndex:20}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:'rgba(255,255,255,0.95)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'move',boxShadow:'0 1px 3px rgba(0,0,0,.25)',border:'1px solid #d1d5db'}}>
                          <svg viewBox="0 0 24 24" style={{width:14,height:14}} fill="none"><path d="M8 5v14M12 5v14M16 5v14" stroke="#555" strokeWidth="2" strokeLinecap="round"/></svg>
                        </div>
                      </div>
                      {/* כוכב – תמונה ראשית */}
                      <div style={{position:'absolute',top:4,right:4,zIndex:20}}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleSetAsPrimary(item._gid); }}
                          style={{width:28,height:28,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',boxShadow:'0 1px 3px rgba(0,0,0,.25)',background: i === 0 ? '#fbbf24' : 'rgba(255,255,255,0.95)',border: i === 0 ? '2px solid #f59e0b' : '1px solid #d1d5db',padding:0,outline:'none'}}
                          title={i === 0 ? 'תמונה ראשית' : 'קבע כתמונה ראשית'}
                        >
                          <svg viewBox="0 0 24 24" style={{width:16,height:16}} fill={i === 0 ? '#fff' : 'none'}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke={i === 0 ? '#fff' : '#6b7280'} strokeWidth="2" strokeLinejoin="round"/></svg>
                        </button>
                      </div>
                      {/* סיבוב + מחיקה */}
                      <div style={{position:'absolute',bottom:4,left:4,zIndex:20,display:'flex',alignItems:'center',gap:6}}>
                        <button
                          type="button"
                          onClick={() => handleRotateImage(i)}
                          disabled={item._kind === 'old' ? rotatingOldIndexes.includes(item.id) : rotatingNewIndexes.includes(item._gid)}
                          style={{width:28,height:28,borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',boxShadow:'0 1px 2px rgba(0,0,0,.15)'}}
                          title="סובב תמונה"
                        >
                          <RotateIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(i)}
                          style={{width:28,height:28,borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',boxShadow:'0 1px 2px rgba(0,0,0,.15)',padding:0}}
                          title="מחק תמונה"
                        >
                          <svg viewBox="0 0 24 24" style={{width:14,height:14}} fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                        </button>
                      </div>
                    </div>
                  ))}

                </div>
              </div>
              <div className="flex items-center justify-center gap-4 popup-footer">
                <button onClick={prevStep} type="button" className="flex items-center gap-1 rounded-full border border-gray-400 px-4 py-2 text-sm font-semibold">
                  <span className="text-lg leading-none">‹</span> קודם
                </button>
                <button
                 onClick={handleMoveToVideo}
                  type="button"
                  style={{ color: "white" }}
                  className="flex items-center gap-1 rounded-full bg-red-600 px-4 py-2 text-white text-sm font-semibold"
                >
                  הבא <span className="text-lg leading-none">›</span>
                </button>
              </div>
            </div>
          )}
          {/* Step 3: וידאו */}
          {step === 3 && (
            <div dir="rtl" className="min-h-screen bg-white flex flex-col items-center py-5 px-4">
              <h2 className="add-car-title">וידאו רכב</h2>
              <h3 className="sub-title-upload-images">צלם או העלה וידאו</h3>
              <div className="w-full flex flex-row py-10">
                <label className="w-1/3 border border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center upload-area2 cursor-pointer">
                  <input type="file" accept="video/mp4,video/x-m4v,video/*" className="hidden" onChange={handleVideoChange} />
                  <b>גרור או לחץ כדי להוסיף וידאו</b>
                  <div className="mb-2 mt-3">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 16l4-5h-3V4h-2v7H8z" />
                      <path d="M5 20h14v-2H5z" />
                    </svg>
                  </div>
                  <p className="text-xs text-red-500 text-center">
                    הקובץ יכול להיות בפורמטים<br />
                    MPEG 4, MOV, AVI
                  </p>
                </label>
                <div className="grid gap-1 flex-1">
                  <div className="relative bg-gray-200" style={{ height: "32vh" }}>
<div className="flex justify-center items-center h-full w-full">
  {videoFile ? (
    <video
      controls
      src={URL.createObjectURL(videoFile)}
      className="w-full h-full object-cover rounded"
    />
  ) : videoUrl ? (
    <video
      controls
      src={videoUrl.startsWith('http') ? videoUrl : API_DOMAIN + '/' + videoUrl}
      className="w-full h-full object-cover rounded"
    />
  ) : (
    <img src="/images/viedio-icon.svg" alt="video placeholder" />
  )}
</div>

                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4 popup-footer">
                <button onClick={prevStep} type="button" className="flex items-center gap-1 rounded-full border border-gray-400 px-4 py-2 text-sm font-semibold">
                  <span className="text-lg leading-none">‹</span> קודם
                </button>
                <button
                  onClick={nextStep}
                  type="button"
                  style={{ color: "white" }}
                  className="flex items-center gap-1 rounded-full bg-red-600 px-4 py-2 text-white text-sm font-semibold"
                >
                  הבא <span className="text-lg leading-none">›</span>
                </button>
              </div>
            </div>
          )}
          {/* Step 4: מחיר ופרטים נוספים */}
          {step === 4 && (
            <div className="popup-content" dir="rtl">
              <h2 className="add-car-title">מחיר ופרטים נוספים</h2>
              <h3 className="sub-title-upload-images">עדכן פרטי מחיר</h3>
              <div dir="rtl" className="m-9">
                <div className="flex items-center justify-center gap-4">
                  <span className="text-sm font-medium">מיועד&nbsp;ל</span>
                  <label className="flex items-center gap-1 cursor-pointer text-sm select-none">
                    <input type="checkbox" checked={form.saleTypes.includes("unload")} onChange={() => toggleSaleType("unload")} className="peer hidden" />
                    <span className="w-4 h-4 rounded border border-gray-400 peer-checked:bg-red-600 peer-checked:border-red-600 grid place-content-center transition">
                      <svg className="opacity-0 peer-checked:opacity-100 w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    לפרוק
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer text-sm select-none">
                    <input type="checkbox" checked={form.saleTypes?.includes("marketing")} onChange={() => toggleSaleType("marketing")} className="peer hidden" />
                    <span className="w-4 h-4 rounded border border-gray-400 peer-checked:bg-red-600 peer-checked:border-red-600 grid place-content-center transition">
                      <svg className="opacity-0 peer-checked:opacity-100 w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    לשיקום
                  </label>
                </div>
                <div className="flex flex-row-reverse items-center gap-3 justify-center w-fit mx-auto m-5">
                  <input
                    id="asking_price"
                    name="asking_price"
                    type="number"
                    dir="ltr"
                    value={car.asking_price}
                    onChange={(e) => setCar((prev) => ({ ...prev, asking_price: e.target.value }))}
                    className="w-40 rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                  <label htmlFor="price" className="text-sm font-medium shrink-0 whitespace-nowrap">מחיר</label>
                </div>
                <div className="flex items-center justify-center gap-4 popup-footer">
                  <button onClick={prevStep} type="button" className="flex items-center gap-1 rounded-full border border-gray-400 px-4 py-2 text-sm font-semibold">
                    <span className="text-lg leading-none">‹</span> קודם
                  </button>
                  <button
                    onClick={nextStep}
                    type="button"
                    style={{ color: "white" }}
                    className="flex items-center gap-1 rounded-full bg-red-600 px-4 py-2 text-white text-sm font-semibold"
                  >
                    הבא <span className="text-lg leading-none">›</span>
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Step 5: סיכום */}
          {step === 5 && (
            <div className="popup-content" dir="rtl">
              <h2 className="add-car-title">סיכום מסמך</h2>
              <h3 className="sub-title-upload-images">כל הפרטים על המודעת רכב</h3>
              <div className="flex justify-center mt-3">
                <div className="w-[30%] flex justify-center" style={{ display: "flex", justifyContent: "center" }}>
                  <CarCardTemplate
                    {...car}
                    images={galleryItems.map((item) =>
                      item._kind === "old"
                        ? { image_url: item.image_url }
                        : { image_url: URL.createObjectURL(item.file) }
                    )}
                    offer_status={0}
                  />
                </div>
              </div>
              <div className="flex items-center justify-center gap-4 popup-footer">
                <button onClick={prevStep} type="button" className="flex items-center gap-1 rounded-full border border-gray-400 px-4 py-2 text-sm font-semibold">
                  <span className="text-lg leading-none">‹</span> קודם
                </button>
                <button
                  onClick={handleSubmit}
                  type="button"
                  style={{ color: "white" }}
                  className="flex items-center gap-1 rounded-full bg-red-600 px-6 py-2 text-white text-sm font-semibold"
                >
                  שמור וסיים <span className="text-lg leading-none text-white">›</span>
                </button>
              </div>
            </div>
          )}
          <div style={{
            display: "flex",
            justifyContent: "center",
            position: "absolute",
            bottom: "0",
            maxWidth: "90%",
            pointerEvents: "none",
          }}>
            <img src={`/images/step${step}.svg`} alt={`שלב ${step}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditCar;
